import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Papa from 'papaparse';
import { format } from 'date-fns';
import { workoutService, nutritionService, sleepService } from './trackingService';

/**
 * Export Service for generating PDF and CSV reports
 */

/**
 * Fetch wellness data based on selected metrics and date range
 */
export const fetchWellnessData = async (userId, dateRange, selectedMetrics) => {
  const { startDate, endDate } = dateRange;
  const data = {};

  try {
    if (selectedMetrics?.includes('activity') || selectedMetrics?.includes('steps')) {
      data.workouts = await workoutService?.getWorkouts(userId, startDate, endDate);
    }

    if (selectedMetrics?.includes('nutrition') || selectedMetrics?.includes('calories')) {
      data.nutrition = await nutritionService?.getNutritionLogs(userId, startDate, endDate);
    }

    if (selectedMetrics?.includes('sleep')) {
      data.sleep = await sleepService?.getSleepLogs(userId, startDate, endDate);
    }

    return data;
  } catch (error) {
    console.error('Error fetching wellness data:', error);
    throw error;
  }
};

/**
 * Calculate summary statistics from wellness data
 */
const calculateSummaryStats = (data, selectedMetrics) => {
  const stats = {};

  if (data?.workouts && selectedMetrics?.includes('activity')) {
    const totalWorkouts = data?.workouts?.length;
    const completedWorkouts = data?.workouts?.filter(w => w?.is_completed)?.length;
    const totalSets = data?.workouts?.reduce((sum, w) => sum + (w?.completed_sets || 0), 0);
    
    stats.activity = {
      totalWorkouts,
      completedWorkouts,
      completionRate: totalWorkouts > 0 ? ((completedWorkouts / totalWorkouts) * 100)?.toFixed(1) : 0,
      totalSets
    };
  }

  if (data?.nutrition && selectedMetrics?.includes('nutrition')) {
    const totalCalories = data?.nutrition?.reduce((sum, n) => sum + (n?.calories || 0), 0);
    const totalProtein = data?.nutrition?.reduce((sum, n) => sum + (n?.protein || 0), 0);
    const totalCarbs = data?.nutrition?.reduce((sum, n) => sum + (n?.carbs || 0), 0);
    const totalFat = data?.nutrition?.reduce((sum, n) => sum + (n?.fat || 0), 0);
    const avgCalories = data?.nutrition?.length > 0 ? (totalCalories / data?.nutrition?.length)?.toFixed(0) : 0;

    stats.nutrition = {
      totalCalories: totalCalories?.toFixed(0),
      avgCalories,
      totalProtein: totalProtein?.toFixed(1),
      totalCarbs: totalCarbs?.toFixed(1),
      totalFat: totalFat?.toFixed(1),
      totalMeals: data?.nutrition?.length
    };
  }

  if (data?.sleep && selectedMetrics?.includes('sleep')) {
    const totalSleepHours = data?.sleep?.reduce((sum, s) => sum + (s?.duration_hours || 0), 0);
    const avgSleepHours = data?.sleep?.length > 0 ? (totalSleepHours / data?.sleep?.length)?.toFixed(1) : 0;
    const avgQualityScore = data?.sleep?.length > 0 
      ? (data?.sleep?.reduce((sum, s) => sum + (s?.quality_score || 0), 0) / data?.sleep?.length)?.toFixed(1) 
      : 0;
    const avgDeepSleep = data?.sleep?.length > 0
      ? (data?.sleep?.reduce((sum, s) => sum + (s?.deep_sleep_hours || 0), 0) / data?.sleep?.length)?.toFixed(1)
      : 0;

    stats.sleep = {
      totalNights: data?.sleep?.length,
      totalHours: totalSleepHours?.toFixed(1),
      avgHours: avgSleepHours,
      avgQuality: avgQualityScore,
      avgDeepSleep
    };
  }

  return stats;
};

/**
 * Generate PDF report
 */
export const generatePDFReport = async (userId, options) => {
  const { dateRange, selectedMetrics, includeCharts = true, includeSummary = true } = options;

  try {
    // Fetch data
    const data = await fetchWellnessData(userId, dateRange, selectedMetrics);
    const stats = calculateSummaryStats(data, selectedMetrics);

    // Create PDF
    const doc = new jsPDF();
    let yPosition = 20;

    // Header
    doc?.setFontSize(22);
    doc?.setTextColor(59, 130, 246); // Primary color
    doc?.text('Wellness Progress Report', 20, yPosition);
    yPosition += 10;

    doc?.setFontSize(10);
    doc?.setTextColor(100, 100, 100);
    doc?.text(`Generated: ${format(new Date(), 'PPP')}`, 20, yPosition);
    yPosition += 5;
    doc?.text(`Period: ${format(new Date(dateRange?.startDate), 'PP')} - ${format(new Date(dateRange?.endDate), 'PP')}`, 20, yPosition);
    yPosition += 15;

    // Summary Section
    if (includeSummary) {
      doc?.setFontSize(16);
      doc?.setTextColor(0, 0, 0);
      doc?.text('Executive Summary', 20, yPosition);
      yPosition += 10;

      doc?.setFontSize(10);
      doc?.setTextColor(60, 60, 60);

      if (stats?.activity) {
        doc?.text(`Activity: ${stats?.activity?.completedWorkouts} workouts completed (${stats?.activity?.completionRate}% completion rate)`, 25, yPosition);
        yPosition += 6;
      }

      if (stats?.nutrition) {
        doc?.text(`Nutrition: ${stats?.nutrition?.totalMeals} meals logged, ${stats?.nutrition?.avgCalories} avg daily calories`, 25, yPosition);
        yPosition += 6;
      }

      if (stats?.sleep) {
        doc?.text(`Sleep: ${stats?.sleep?.avgHours}h average per night, ${stats?.sleep?.avgQuality}/10 quality score`, 25, yPosition);
        yPosition += 6;
      }

      yPosition += 10;
    }

    // Activity Data Table
    if (data?.workouts && selectedMetrics?.includes('activity')) {
      doc?.setFontSize(14);
      doc?.setTextColor(0, 0, 0);
      doc?.text('Activity Tracking', 20, yPosition);
      yPosition += 8;

      const workoutTableData = data?.workouts?.map(w => [
        format(new Date(w?.workout_date), 'PP'),
        w?.workout_name || 'Workout',
        `${w?.completed_sets || 0}`,
        w?.is_completed ? 'Yes' : 'No'
      ]);

      doc?.autoTable({
        startY: yPosition,
        head: [['Date', 'Workout', 'Sets', 'Completed']],
        body: workoutTableData,
        theme: 'striped',
        headStyles: { fillColor: [59, 130, 246] },
        margin: { left: 20, right: 20 }
      });

      yPosition = doc?.lastAutoTable?.finalY + 15;
    }

    // Nutrition Data Table
    if (data?.nutrition && selectedMetrics?.includes('nutrition')) {
      if (yPosition > 250) {
        doc?.addPage();
        yPosition = 20;
      }

      doc?.setFontSize(14);
      doc?.setTextColor(0, 0, 0);
      doc?.text('Nutrition Tracking', 20, yPosition);
      yPosition += 8;

      const nutritionTableData = data?.nutrition?.map(n => [
        format(new Date(n?.logged_date), 'PP'),
        n?.meal_type || 'Meal',
        n?.calories?.toString() || '0',
        `${n?.protein || 0}g`,
        `${n?.carbs || 0}g`,
        `${n?.fat || 0}g`
      ]);

      doc?.autoTable({
        startY: yPosition,
        head: [['Date', 'Meal Type', 'Calories', 'Protein', 'Carbs', 'Fat']],
        body: nutritionTableData,
        theme: 'striped',
        headStyles: { fillColor: [16, 185, 129] },
        margin: { left: 20, right: 20 }
      });

      yPosition = doc?.lastAutoTable?.finalY + 15;
    }

    // Sleep Data Table
    if (data?.sleep && selectedMetrics?.includes('sleep')) {
      if (yPosition > 250) {
        doc?.addPage();
        yPosition = 20;
      }

      doc?.setFontSize(14);
      doc?.setTextColor(0, 0, 0);
      doc?.text('Sleep Analysis', 20, yPosition);
      yPosition += 8;

      const sleepTableData = data?.sleep?.map(s => [
        format(new Date(s?.sleep_date), 'PP'),
        `${s?.duration_hours || 0}h`,
        `${s?.quality_score || 0}/10`,
        `${s?.deep_sleep_hours || 0}h`,
        s?.notes || '-'
      ]);

      doc?.autoTable({
        startY: yPosition,
        head: [['Date', 'Duration', 'Quality', 'Deep Sleep', 'Notes']],
        body: sleepTableData,
        theme: 'striped',
        headStyles: { fillColor: [139, 92, 246] },
        margin: { left: 20, right: 20 }
      });
    }

    // Footer
    const pageCount = doc?.internal?.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc?.setPage(i);
      doc?.setFontSize(8);
      doc?.setTextColor(150, 150, 150);
      doc?.text(
        `Page ${i} of ${pageCount} | PrimeOne+ Wellness Report`,
        doc?.internal?.pageSize?.getWidth() / 2,
        doc?.internal?.pageSize?.getHeight() - 10,
        { align: 'center' }
      );
    }

    // Save PDF
    const fileName = `wellness-report-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
    doc?.save(fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

/**
 * Generate CSV export
 */
export const generateCSVReport = async (userId, options) => {
  const { dateRange, selectedMetrics } = options;

  try {
    // Fetch data
    const data = await fetchWellnessData(userId, dateRange, selectedMetrics);
    const csvData = [];

    // Activity data
    if (data?.workouts && selectedMetrics?.includes('activity')) {
      csvData?.push(['Activity Tracking']);
      csvData?.push(['Date', 'Workout Name', 'Completed Sets', 'Total Sets', 'Completed']);
      
      data?.workouts?.forEach(w => {
        csvData?.push([
          format(new Date(w?.workout_date), 'yyyy-MM-dd'),
          w?.workout_name || 'Workout',
          w?.completed_sets || 0,
          w?.is_completed ? 'Yes' : 'No'
        ]);
      });
      
      csvData?.push([]); // Empty row separator
    }

    // Nutrition data
    if (data?.nutrition && selectedMetrics?.includes('nutrition')) {
      csvData?.push(['Nutrition Tracking']);
      csvData?.push(['Date', 'Meal Type', 'Calories', 'Protein (g)', 'Carbs (g)', 'Fat (g)', 'Portion Multiplier']);
      
      data?.nutrition?.forEach(n => {
        csvData?.push([
          format(new Date(n?.logged_date), 'yyyy-MM-dd'),
          n?.meal_type || 'Meal',
          n?.calories || 0,
          n?.protein || 0,
          n?.carbs || 0,
          n?.fat || 0,
          n?.portion_multiplier || 1.0
        ]);
      });
      
      csvData?.push([]); // Empty row separator
    }

    // Sleep data
    if (data?.sleep && selectedMetrics?.includes('sleep')) {
      csvData?.push(['Sleep Analysis']);
      csvData?.push(['Date', 'Bedtime', 'Wake Time', 'Duration (h)', 'Deep Sleep (h)', 'Light Sleep (h)', 'REM Sleep (h)', 'Quality Score', 'Consistency Score', 'Notes']);
      
      data?.sleep?.forEach(s => {
        csvData?.push([
          format(new Date(s?.sleep_date), 'yyyy-MM-dd'),
          s?.bedtime || '-',
          s?.wake_time || '-',
          s?.duration_hours || 0,
          s?.deep_sleep_hours || 0,
          s?.light_sleep_hours || 0,
          s?.rem_sleep_hours || 0,
          s?.quality_score || 0,
          s?.consistency_score || 0,
          s?.notes || ''
        ]);
      });
    }

    // Convert to CSV string
    const csv = Papa?.unparse(csvData);

    // Create and download file
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const fileName = `wellness-data-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    
    link?.setAttribute('href', url);
    link?.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);

    return { success: true, fileName };
  } catch (error) {
    console.error('Error generating CSV:', error);
    throw error;
  }
};